import type { JsonValue } from '../types';
import { inferType } from './json-path';

const canMatchShape = (a: JsonValue, b: JsonValue): boolean => {
  if (inferType(a) !== inferType(b)) return false;

  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    if (Array.isArray(a) || Array.isArray(b)) return true;
    return Object.keys(a as Record<string, JsonValue>).every((key) =>
      Object.prototype.hasOwnProperty.call(b as Record<string, JsonValue>, key),
    );
  }

  return true;
};

/**
 * Longest-common-subsequence style matching between `original` and `next`
 * arrays, using `canMatchShape` as the compatibility predicate. Preserves
 * relative order, correctly handles insertions/removals/reorders anywhere
 * (unlike a naive greedy single-pass scan, which can silently let a removed
 * item slip through unnoticed as "just didn't match, skip it").
 */
const matchArrays = (original: JsonValue[], next: JsonValue[]): Array<[number, number]> => {
  const n = original.length;
  const m = next.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));

  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = canMatchShape(original[i], next[j])
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (canMatchShape(original[i], next[j]) && dp[i][j] === dp[i + 1][j + 1] + 1) {
      pairs.push([i, j]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }

  return pairs;
};

const validateArray = (original: JsonValue[], next: JsonValue[], path: string): string | null => {
  const pairs = matchArrays(original, next);
  const matchedNextIndexes = new Set(pairs.map(([, nextIndex]) => nextIndex));

  for (const [originalIndex, nextIndex] of pairs) {
    const error = validateNode(original[originalIndex], next[nextIndex], `${path}[${nextIndex}]`);
    if (error) return error;
  }

  for (let nextIndex = 0; nextIndex < next.length; nextIndex += 1) {
    if (matchedNextIndexes.has(nextIndex)) continue;

    const isNewItemValid = original.length === 0 || original.some((item) => canMatchShape(item, next[nextIndex]));
    if (!isNewItemValid) {
      return `New array item does not match any existing item shape at ${path}[${nextIndex}]`;
    }
  }

  return null;
};

const validateNode = (original: JsonValue, next: JsonValue, path: string): string | null => {
  if (inferType(original) !== inferType(next)) {
    return `Data type cannot change at ${path}`;
  }

  if (Array.isArray(original) && Array.isArray(next)) {
    return validateArray(original, next, path);
  }

  if (original !== null && next !== null && typeof original === 'object' && typeof next === 'object') {
    const nextObject = next as Record<string, JsonValue>;

    for (const [key, originalValue] of Object.entries(original as Record<string, JsonValue>)) {
      if (!Object.prototype.hasOwnProperty.call(nextObject, key)) {
        return `Existing key cannot be removed: ${path}.${key}`;
      }

      const error = validateNode(originalValue, nextObject[key], `${path}.${key}`);
      if (error) return error;
    }
  }

  return null;
};

/**
 * The baked-in safe-edit policy: object keys may be added but not removed;
 * array items may be added (as a shape-matching template clone) or removed
 * but surviving items must keep their original shape/type recursively; no
 * value's JSON type may change. Used internally by SafeJsonEditor whenever
 * `canEditStructure` is false, and exported for consumers who want to
 * pre-validate a change themselves (e.g. before persisting).
 */
export const validateSafeUpdate = (original: JsonValue, next: JsonValue): string | null =>
  validateNode(original, next, 'root');
